import { useState, useEffect } from "react";
import {
  Search, Store, MapPin, Clock, ArrowRight, Plus, X, CheckCircle, AlertCircle,
  HardHat, Briefcase, Wrench, Package, Handshake, Megaphone, Send, Filter,
  Award, Zap, ChevronDown
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

import { SEO } from "../components/SEO";
import { NavbarLight } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TABS = [
  { id: "providers", label: "Service Providers", icon: Store },
  { id: "opportunities", label: "Opportunity Exchange", icon: Briefcase },
  { id: "construction", label: "Construction Hub", icon: HardHat },
  { id: "signals", label: "Request / Offer", icon: Megaphone },
];

// ==================== FOUNDING PARTNERS BANNER ====================
const FoundingPartners = ({ listings }) => {
  const founders = listings.filter(l => l.is_founding_partner || l.is_featured);
  if (!founders.length) return null;
  return (
    <section className="bg-[#1A2F23] py-10 px-6 md:px-12" data-testid="founding-partners-section">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-2 mb-6">
          <Award className="w-5 h-5 text-[#D4A017]" />
          <h2 className="font-heading text-xl font-semibold text-white">Founding EK Valleys Pilot Partners</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {founders.map(f => (
            <div key={f.id} className="bg-white/5 border border-[#D4A017]/30 rounded-xl p-5" data-testid={`founding-card-${f.id}`}>
              <div className="flex items-start justify-between mb-2">
                <h3 className="font-semibold text-white text-base">{f.name}</h3>
                <Badge className="bg-[#D4A017]/20 text-[#D4A017] border-[#D4A017]/40 text-[10px]">
                  {f.is_founding_partner ? "Founding Partner" : "Featured"}
                </Badge>
              </div>
              <p className="text-white/50 text-xs mb-2"><MapPin className="w-3 h-3 inline mr-1" />{f.town} | {f.category}</p>
              <p className="text-white/70 text-sm line-clamp-2">{f.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ==================== LISTING CARD ====================
const ListingCard = ({ item, type }) => {
  const isSignal = type === "signal";
  const isConstruction = type === "construction";
  return (
    <div className={`bg-white border-2 ${isConstruction ? "border-[#D4A017]/30" : "border-[#D1CFC5]"} rounded-xl p-5 hover:shadow-md transition-shadow`} data-testid={`${type}-card-${item.id}`}>
      <div className="flex items-start justify-between mb-2">
        <h3 className="font-heading text-base font-semibold text-[#1A1915] line-clamp-1">
          {item.name || item.title}
        </h3>
        {isSignal && (
          <Badge className={`text-[10px] ${item.signal_type === "offer" ? "bg-[#2C4A3B] text-white" : "bg-[#3B82F6] text-white"}`}>
            {item.signal_type === "offer" ? "Offering" : "Requesting"}
          </Badge>
        )}
        {!isSignal && item.post_type && (
          <Badge className="bg-[#F7F6F2] text-[#5C5A52] border-[#D1CFC5] text-[10px]">{item.post_type}</Badge>
        )}
        {!isSignal && !item.post_type && item.category && (
          <Badge className="bg-[#F7F6F2] text-[#5C5A52] border-[#D1CFC5] text-[10px]">{item.category}</Badge>
        )}
        {item.is_founding_partner && (
          <Badge className="bg-[#D4A017]/20 text-[#D4A017] border-[#D4A017]/40 text-[10px] ml-1">Founding</Badge>
        )}
      </div>
      <div className="flex items-center gap-3 text-xs text-[#5C5A52] mb-3">
        <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{item.town}</span>
        {item.expires_at && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />Expires {new Date(item.expires_at).toLocaleDateString()}</span>}
      </div>
      <p className="text-[#5C5A52] text-sm line-clamp-3 mb-3">{item.description}</p>
      <div className="flex items-center gap-3 text-xs text-[#5C5A52]">
        {item.contact_email && <a href={`mailto:${item.contact_email}`} className="text-[#2C4A3B] hover:underline font-medium">Email</a>}
        {item.contact_phone && <a href={`tel:${item.contact_phone}`} className="text-[#2C4A3B] hover:underline font-medium">Call</a>}
        {item.website_url && <a href={item.website_url} target="_blank" rel="noreferrer" className="text-[#2C4A3B] hover:underline font-medium">Website</a>}
      </div>
    </div>
  );
};

// ==================== SUBMISSION FORMS ====================
const ListingForm = ({ config, onClose, onSubmit }) => {
  const [form, setForm] = useState({ name: "", category: "", town: "", description: "", contact_email: "", contact_phone: "", website_url: "", social_link: "" });
  const [submitting, setSubmitting] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = async () => {
    if (!form.name || !form.category || !form.town || !form.description) { toast.error("Please fill in all required fields"); return; }
    if (form.description.length > 250) { toast.error("Description must be 250 characters or less"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/marketplace/listings`, form);
      toast.success("Listing submitted for review!");
      onSubmit();
      onClose();
    } catch (e) { toast.error("Failed to submit listing"); }
    setSubmitting(false);
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div><Label>Business / Provider Name *</Label><Input value={form.name} onChange={e => set("name", e.target.value)} data-testid="listing-name" /></div>
        <div><Label>Category *</Label>
          <Select value={form.category} onValueChange={v => set("category", v)}>
            <SelectTrigger data-testid="listing-category"><SelectValue placeholder="Select category" /></SelectTrigger>
            <SelectContent>{config.categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div><Label>Town *</Label>
          <Select value={form.town} onValueChange={v => set("town", v)}>
            <SelectTrigger data-testid="listing-town"><SelectValue placeholder="Select town" /></SelectTrigger>
            <SelectContent>{config.towns.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>Email</Label><Input type="email" value={form.contact_email} onChange={e => set("contact_email", e.target.value)} data-testid="listing-email" /></div>
      </div>
      <div><Label>Description * (max 250 chars)</Label><Textarea value={form.description} onChange={e => set("description", e.target.value)} maxLength={250} data-testid="listing-desc" /><p className="text-xs text-[#5C5A52] mt-1">{form.description.length}/250</p></div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div><Label>Phone</Label><Input value={form.contact_phone} onChange={e => set("contact_phone", e.target.value)} /></div>
        <div><Label>Website URL</Label><Input value={form.website_url} onChange={e => set("website_url", e.target.value)} /></div>
      </div>
      <Button onClick={handleSubmit} disabled={submitting} className="w-full bg-[#2C4A3B] hover:bg-[#1A2F23] text-white" data-testid="submit-listing-btn">
        {submitting ? "Submitting..." : "Submit Listing for Review"}
      </Button>
    </div>
  );
};

const OpportunityForm = ({ config, onClose, onSubmit }) => {
  const [form, setForm] = useState({ title: "", description: "", post_type: "", town: "", contact_email: "", contact_phone: "", expires_at: "" });
  const [submitting, setSubmitting] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = async () => {
    if (!form.title || !form.post_type || !form.town || !form.description) { toast.error("Please fill in all required fields"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/marketplace/opportunities`, { ...form, expires_at: form.expires_at || null });
      toast.success("Opportunity submitted for review!");
      onSubmit();
      onClose();
    } catch (e) { toast.error("Failed to submit opportunity"); }
    setSubmitting(false);
  };

  return (
    <div className="space-y-4">
      <div><Label>Title *</Label><Input value={form.title} onChange={e => set("title", e.target.value)} data-testid="opp-title" /></div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div><Label>Type *</Label>
          <Select value={form.post_type} onValueChange={v => set("post_type", v)}>
            <SelectTrigger data-testid="opp-type"><SelectValue placeholder="Select type" /></SelectTrigger>
            <SelectContent>{config.opportunity_types.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>Town *</Label>
          <Select value={form.town} onValueChange={v => set("town", v)}>
            <SelectTrigger data-testid="opp-town"><SelectValue placeholder="Select town" /></SelectTrigger>
            <SelectContent>{config.towns.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div><Label>Description *</Label><Textarea value={form.description} onChange={e => set("description", e.target.value)} data-testid="opp-desc" /></div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div><Label>Email</Label><Input type="email" value={form.contact_email} onChange={e => set("contact_email", e.target.value)} /></div>
        <div><Label>Phone</Label><Input value={form.contact_phone} onChange={e => set("contact_phone", e.target.value)} /></div>
        <div><Label>Expiry Date</Label><Input type="date" value={form.expires_at} onChange={e => set("expires_at", e.target.value)} /></div>
      </div>
      <Button onClick={handleSubmit} disabled={submitting} className="w-full bg-[#2C4A3B] hover:bg-[#1A2F23] text-white" data-testid="submit-opp-btn">
        {submitting ? "Submitting..." : "Post Opportunity for Review"}
      </Button>
    </div>
  );
};

const ConstructionForm = ({ config, onClose, onSubmit }) => {
  const [form, setForm] = useState({ title: "", description: "", post_type: "", town: "", contact_email: "", contact_phone: "", expires_at: "" });
  const [submitting, setSubmitting] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = async () => {
    if (!form.title || !form.post_type || !form.town || !form.description) { toast.error("Please fill in all required fields"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/marketplace/construction`, { ...form, expires_at: form.expires_at || null });
      toast.success("Construction post submitted for review!");
      onSubmit();
      onClose();
    } catch (e) { toast.error("Failed to submit post"); }
    setSubmitting(false);
  };

  return (
    <div className="space-y-4">
      <div><Label>Title *</Label><Input value={form.title} onChange={e => set("title", e.target.value)} data-testid="const-title" /></div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div><Label>Type *</Label>
          <Select value={form.post_type} onValueChange={v => set("post_type", v)}>
            <SelectTrigger data-testid="const-type"><SelectValue placeholder="Select type" /></SelectTrigger>
            <SelectContent>{config.construction_types.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>Town *</Label>
          <Select value={form.town} onValueChange={v => set("town", v)}>
            <SelectTrigger data-testid="const-town"><SelectValue placeholder="Select town" /></SelectTrigger>
            <SelectContent>{config.towns.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div><Label>Description *</Label><Textarea value={form.description} onChange={e => set("description", e.target.value)} data-testid="const-desc" /></div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div><Label>Email</Label><Input type="email" value={form.contact_email} onChange={e => set("contact_email", e.target.value)} /></div>
        <div><Label>Phone</Label><Input value={form.contact_phone} onChange={e => set("contact_phone", e.target.value)} /></div>
        <div><Label>Expiry Date</Label><Input type="date" value={form.expires_at} onChange={e => set("expires_at", e.target.value)} /></div>
      </div>
      <Button onClick={handleSubmit} disabled={submitting} className="w-full bg-[#D4A017] hover:bg-[#E8B731] text-[#1A1915] font-bold" data-testid="submit-const-btn">
        {submitting ? "Submitting..." : "Post to Construction Hub"}
      </Button>
    </div>
  );
};

const SignalForm = ({ config, onClose, onSubmit }) => {
  const [form, setForm] = useState({ signal_type: "request", title: "", description: "", category: "", contact_email: "", contact_phone: "", expires_at: "" });
  const [submitting, setSubmitting] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = async () => {
    if (!form.title || !form.category || !form.description) { toast.error("Please fill in all required fields"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/marketplace/signals`, { ...form, expires_at: form.expires_at || null });
      toast.success("Signal posted for review!");
      onSubmit();
      onClose();
    } catch (e) { toast.error("Failed to post signal"); }
    setSubmitting(false);
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        {["request", "offer"].map(t => (
          <button key={t} type="button" onClick={() => set("signal_type", t)}
            className={`flex-1 py-3 rounded-lg font-semibold text-sm border-2 transition-all ${form.signal_type === t ? (t === "request" ? "bg-[#3B82F6] text-white border-[#3B82F6]" : "bg-[#2C4A3B] text-white border-[#2C4A3B]") : "border-[#D1CFC5] text-[#5C5A52]"}`}
            data-testid={`signal-type-${t}`}
          >
            {t === "request" ? "I'm Looking For..." : "I'm Offering..."}
          </button>
        ))}
      </div>
      <div><Label>Title *</Label><Input value={form.title} onChange={e => set("title", e.target.value)} placeholder={form.signal_type === "request" ? "e.g. Need electrician for residential project" : "e.g. Journeyman carpenter available"} data-testid="signal-title" /></div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div><Label>Category *</Label>
          <Select value={form.category} onValueChange={v => set("category", v)}>
            <SelectTrigger data-testid="signal-category"><SelectValue placeholder="Select category" /></SelectTrigger>
            <SelectContent>{config.categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>Expiry Date</Label><Input type="date" value={form.expires_at} onChange={e => set("expires_at", e.target.value)} /></div>
      </div>
      <div><Label>Description *</Label><Textarea value={form.description} onChange={e => set("description", e.target.value)} data-testid="signal-desc" /></div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div><Label>Email</Label><Input type="email" value={form.contact_email} onChange={e => set("contact_email", e.target.value)} /></div>
        <div><Label>Phone</Label><Input value={form.contact_phone} onChange={e => set("contact_phone", e.target.value)} /></div>
      </div>
      <Button onClick={handleSubmit} disabled={submitting} className="w-full bg-[#2C4A3B] hover:bg-[#1A2F23] text-white" data-testid="submit-signal-btn">
        {submitting ? "Submitting..." : "Post Signal for Review"}
      </Button>
    </div>
  );
};

// ==================== MAIN PAGE ====================
const Marketplace = () => {
  const [activeTab, setActiveTab] = useState("providers");
  const [config, setConfig] = useState({ categories: [], opportunity_types: [], construction_types: [], towns: [] });
  const [listings, setListings] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [construction, setConstruction] = useState([]);
  const [signals, setSignals] = useState([]);
  const [featuredListings, setFeaturedListings] = useState([]);
  const [search, setSearch] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterTown, setFilterTown] = useState("");
  const [filterType, setFilterType] = useState("");
  const [showForm, setShowForm] = useState(false);

  useEffect(() => { fetchConfig(); }, []);
  useEffect(() => { fetchData(); }, [activeTab, search, filterCategory, filterTown, filterType]);

  const fetchConfig = async () => {
    try {
      const res = await axios.get(`${API}/marketplace/config`);
      setConfig(res.data);
    } catch (e) { console.error(e); }
  };

  const fetchData = async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (filterTown) params.town = filterTown;

      if (activeTab === "providers") {
        if (filterCategory) params.category = filterCategory;
        const [listRes, featRes] = await Promise.all([
          axios.get(`${API}/marketplace/listings`, { params }),
          axios.get(`${API}/marketplace/listings/featured`)
        ]);
        setListings(listRes.data);
        setFeaturedListings(featRes.data);
      } else if (activeTab === "opportunities") {
        if (filterType) params.post_type = filterType;
        const res = await axios.get(`${API}/marketplace/opportunities`, { params });
        setOpportunities(res.data);
      } else if (activeTab === "construction") {
        if (filterType) params.post_type = filterType;
        const res = await axios.get(`${API}/marketplace/construction`, { params });
        setConstruction(res.data);
      } else if (activeTab === "signals") {
        if (filterType) params.signal_type = filterType;
        if (filterCategory) params.category = filterCategory;
        const res = await axios.get(`${API}/marketplace/signals`, { params });
        setSignals(res.data);
      }
    } catch (e) { console.error(e); }
  };

  const clearFilters = () => { setSearch(""); setFilterCategory(""); setFilterTown(""); setFilterType(""); };

  const formTitles = { providers: "List a Service Provider", opportunities: "Post an Opportunity", construction: "Post to Construction Hub", signals: "Post a Request or Offer" };

  const currentData = activeTab === "providers" ? listings : activeTab === "opportunities" ? opportunities : activeTab === "construction" ? construction : signals;
  const currentType = activeTab === "providers" ? "listing" : activeTab === "construction" ? "construction" : activeTab === "signals" ? "signal" : "opportunity";

  return (
    <div className="min-h-screen bg-[#EFEDE5]">
      <SEO
        title="Marketplace & Opportunity Exchange"
        description="EkValleys is a new regional platform connecting businesses, workforce opportunities, and community initiatives across the EK Valleys. Find contractors, post opportunities, and connect with your community."
        keywords="East Kootenay marketplace, EK Valleys, contractors, jobs, opportunities, services, community commerce"
      />
      <NavbarLight />

      {/* Hero */}
      <section className="py-12 md:py-16 px-6 md:px-12 bg-[#1A1915]" data-testid="marketplace-hero">
        <div className="max-w-6xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-[#D4A017]/15 text-[#D4A017] text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">
            <Store className="w-3.5 h-3.5" />
            Community Commerce Hub
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-4">
            Marketplace & Opportunity Exchange
          </h1>
          <p className="text-white/60 text-base md:text-lg max-w-3xl mx-auto">
            EkValleys is a new regional platform being developed to connect businesses, workforce opportunities, and community initiatives across the EK Valleys.
          </p>
        </div>
      </section>

      {/* Founding Partners */}
      <FoundingPartners listings={featuredListings} />

      {/* Tabs */}
      <section className="sticky top-0 z-20 bg-[#EFEDE5] border-b-2 border-[#D1CFC5]" data-testid="marketplace-tabs">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="flex overflow-x-auto gap-0 -mb-[2px]">
            {TABS.map(tab => (
              <button key={tab.id} onClick={() => { setActiveTab(tab.id); clearFilters(); }}
                className={`flex items-center gap-2 px-5 py-4 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${activeTab === tab.id ? "border-[#2C4A3B] text-[#1A1915]" : "border-transparent text-[#5C5A52] hover:text-[#1A1915]"}`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4" />{tab.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Filters + Content */}
      <section className="py-8 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          {/* Toolbar */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mb-6" data-testid="marketplace-toolbar">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C5A52]" />
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..." className="pl-10 bg-white" data-testid="marketplace-search" />
            </div>
            {(activeTab === "providers" || activeTab === "signals") && (
              <Select value={filterCategory} onValueChange={v => setFilterCategory(v === "all" ? "" : v)}>
                <SelectTrigger className="w-full sm:w-[200px] bg-white" data-testid="filter-category"><SelectValue placeholder="All Categories" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {config.categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            {activeTab === "opportunities" && (
              <Select value={filterType} onValueChange={v => setFilterType(v === "all" ? "" : v)}>
                <SelectTrigger className="w-full sm:w-[200px] bg-white" data-testid="filter-type"><SelectValue placeholder="All Types" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {config.opportunity_types.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            {activeTab === "construction" && (
              <Select value={filterType} onValueChange={v => setFilterType(v === "all" ? "" : v)}>
                <SelectTrigger className="w-full sm:w-[200px] bg-white" data-testid="filter-const-type"><SelectValue placeholder="All Types" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {config.construction_types.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            {activeTab === "signals" && (
              <Select value={filterType} onValueChange={v => setFilterType(v === "all" ? "" : v)}>
                <SelectTrigger className="w-full sm:w-[140px] bg-white" data-testid="filter-signal-type"><SelectValue placeholder="All" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="request">Requests</SelectItem>
                  <SelectItem value="offer">Offers</SelectItem>
                </SelectContent>
              </Select>
            )}
            <Select value={filterTown} onValueChange={v => setFilterTown(v === "all" ? "" : v)}>
              <SelectTrigger className="w-full sm:w-[180px] bg-white" data-testid="filter-town"><SelectValue placeholder="All Towns" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Towns</SelectItem>
                {config.towns.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button onClick={() => setShowForm(true)} className="bg-[#2C4A3B] hover:bg-[#1A2F23] text-white" data-testid="post-btn">
              <Plus className="w-4 h-4 mr-1" /> Post
            </Button>
          </div>

          {/* Results */}
          {currentData.length === 0 ? (
            <div className="text-center py-16">
              <Store className="w-12 h-12 text-[#D1CFC5] mx-auto mb-4" />
              <h3 className="font-heading text-xl font-semibold text-[#1A1915] mb-2">
                {activeTab === "providers" ? "No service providers listed yet" : activeTab === "construction" ? "No construction posts yet" : activeTab === "signals" ? "No signals yet" : "No opportunities posted yet"}
              </h3>
              <p className="text-[#5C5A52] mb-6">Be the first to post in this section.</p>
              <Button onClick={() => setShowForm(true)} className="bg-[#D4A017] hover:bg-[#E8B731] text-[#1A1915] font-bold" data-testid="empty-post-btn">
                <Plus className="w-4 h-4 mr-1" /> {formTitles[activeTab]}
              </Button>
            </div>
          ) : (
            <div className={`grid gap-4 ${activeTab === "signals" ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3" : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"}`}>
              {currentData.map(item => (
                <ListingCard key={item.id} item={item} type={currentType} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Submission Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">{formTitles[activeTab]}</DialogTitle>
          </DialogHeader>
          {activeTab === "providers" && <ListingForm config={config} onClose={() => setShowForm(false)} onSubmit={fetchData} />}
          {activeTab === "opportunities" && <OpportunityForm config={config} onClose={() => setShowForm(false)} onSubmit={fetchData} />}
          {activeTab === "construction" && <ConstructionForm config={config} onClose={() => setShowForm(false)} onSubmit={fetchData} />}
          {activeTab === "signals" && <SignalForm config={config} onClose={() => setShowForm(false)} onSubmit={fetchData} />}
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default Marketplace;
